'use client';
import React, { useState, useEffect, useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import EditableText from './EditableText';
import EditableImage from './EditableImage';
import ColorPicker from './ColorPicker';
import { useEditMode } from './EditModeProvider';
import IOSToast from '@/components/ui/IOSToast';

export default function HowItWorksCircular() {
  const [activeIndex, setActiveIndex] = useState(0);
  const [maxUnlockedIndex, setMaxUnlockedIndex] = useState(0);
  const [ringRotation, setRingRotation] = useState(180);
  const { isEditMode } = useEditMode();

  const [activeColor, setActiveColor] = useState('');
  const [ringColor, setRingColor] = useState('');
  const [cardBg, setCardBg] = useState('');

  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');

  // Анимация и отслеживание видимости
  const sectionRef = useRef(null);
  const isInView = useInView(sectionRef, { once: true, amount: 0.6 });
  const [isAnimating, setIsAnimating] = useState(true);

  useEffect(() => {
    fetch('/api/site-contents')
      .then(res => res.json())
      .then(data => {
        if (Array.isArray(data)) {
          const active = data.find(item => item.key === 'how_works_active_color')?.value;
          const ring = data.find(item => item.key === 'how_works_ring_color')?.value;
          const card = data.find(item => item.key === 'how_works_card_bg')?.value;
          if (active) setActiveColor(active);
          if (ring) setRingColor(ring);
          if (card) setCardBg(card);
        } else {
          if (data.how_works_active_color) setActiveColor(data.how_works_active_color);
          if (data.how_works_ring_color) setRingColor(data.how_works_ring_color);
          if (data.how_works_card_bg) setCardBg(data.how_works_card_bg);
        }
      })
      .catch(console.error);
  }, []);

  const handleStepClick = (index) => {
    if (isAnimating) return; // Блокировка во время анимации
    if (index === activeIndex) return;

    if (index > maxUnlockedIndex + 1) {
      setToastMessage('Пожалуйста, проходите этапы по порядку');
      setShowToast(true);
      setTimeout(() => setShowToast(false), 3000);
      return;
    }

    if (index === maxUnlockedIndex + 1) {
      setMaxUnlockedIndex(index);
    }
    
    let target = 180 - index * 72;
    let current = ringRotation;
    
    let diff = (target - current) % 360;
    if (diff > 180) diff -= 360;
    if (diff < -180) diff += 360;
    
    setRingRotation(current + diff);
    setActiveIndex(index);
  };

  const handleNextStep = () => {
    if (isAnimating) return; // Блокировка во время анимации
    const nextIndex = (activeIndex + 1) % steps.length;
    handleStepClick(nextIndex);
  };

  const activeBgStyle = activeColor ? { backgroundColor: activeColor, borderColor: activeColor } : {};
  const activeShadowStyle = activeColor ? { boxShadow: `0 0 25px ${activeColor}80` } : {};
  const activeTextStyle = activeColor ? { color: activeColor } : {};
  const ringStyle = ringColor ? { borderColor: ringColor } : {};

  const getIcon = (type) => {
    const color = '%236B7280';
    const svgs = {
      sensor: `data:image/svg+xml;utf8,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='${color}' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='M12 14v2m-4-2v2m8-2v2M4 10h16v10H4zM12 4v2m-4-2v2m8-2v2'/%3E%3C/svg%3E`,
      controller: `data:image/svg+xml;utf8,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='${color}' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Crect x='4' y='4' width='16' height='16' rx='2' ry='2'/%3E%3Cpath d='M9 9h6v6H9z'/%3E%3C/svg%3E`,
      wifi: `data:image/svg+xml;utf8,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='${color}' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='M5 12.55a11 11 0 0 1 14.08 0M1.42 9a16 16 0 0 1 21.16 0M8.53 16.11a6 6 0 0 1 6.95 0M12 20h.01'/%3E%3C/svg%3E`,
      server: `data:image/svg+xml;utf8,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='${color}' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Crect x='2' y='2' width='20' height='8' rx='2' ry='2'/%3E%3Crect x='2' y='14' width='20' height='8' rx='2' ry='2'/%3E%3Cline x1='6' y1='6' x2='6.01' y2='6'/%3E%3Cline x1='6' y1='18' x2='6.01' y2='18'/%3E%3C/svg%3E`,
      app: `data:image/svg+xml;utf8,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='${color}' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Crect x='5' y='2' width='14' height='20' rx='2' ry='2'/%3E%3Cline x1='12' y1='18' x2='12.01' y2='18'/%3E%3C/svg%3E`,
      folder: `data:image/svg+xml;utf8,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='${color}' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z' /%3E%3C/svg%3E`,
      downloadDoc: `data:image/svg+xml;utf8,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='${color}' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z' /%3E%3Cpolyline points='14 2 14 8 20 8' /%3E%3Cpath d='M12 18v-6' /%3E%3Cpath d='m9 15 3 3 3-3' /%3E%3C/svg%3E`,
      thumbsUp: `data:image/svg+xml;utf8,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='${color}' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='M7 10v12' /%3E%3Cpath d='M15 5.88 14 10h5.83a2 2 0 0 1 1.92 2.56l-2.33 8A2 2 0 0 1 17.5 22H4a2 2 0 0 1-2-2v-8a2 2 0 0 1 2-2h2.76a2 2 0 0 0 1.79-1.11L12 2h0a3.13 3.13 0 0 1 3 3.88Z' /%3E%3C/svg%3E`
    };
    return svgs[type];
  };

  const steps = [
    {
      label: 'Датчики',
      title: 'Датчики',
      desc: 'Датчики устанавливаются в оборудование и помещения и подключаются проводным путем к контроллеру',
      extraTitle: 'Сбор первичных данных',
      extraDesc: 'Высокоточные промышленные датчики непрерывно фиксируют ключевые показатели оборудования: температуру, вибрацию, влажность и другие параметры. Установка производится без вмешательства в конструкцию оборудования, что сохраняет заводскую гарантию.',
      icon: getIcon('wifi')
    },
    {
      label: 'Контроллер',
      title: 'Контроллер',
      desc: 'Мозг системы - контроллер. Он опрашивает датчики в определенное время, обрабатывает данные и передает на Wi-Fi роутер заведения',
      extraTitle: 'Локальная обработка',
      extraDesc: 'Микроконтроллер собирает сигналы с датчиков, осуществляет их первичную фильтрацию и оцифровку. Встроенные алгоритмы позволяют отсеять шумы и подготовить чистые данные для отправки на сервер.',
      icon: getIcon('app')
    },
    {
      label: 'Wi-Fi роутер',
      title: 'Wi-Fi роутер',
      desc: 'Обеспечивает защищенную передачу данных от контроллера на удаленный сервер в режиме реального времени',
      extraTitle: 'Безопасная передача',
      extraDesc: 'Используя существующую инфраструктуру предприятия или выделенные каналы связи, данные передаются по зашифрованным протоколами. Гарантируется целостность пакетов и защита от несанкционированного доступа.',
      icon: getIcon('folder')
    },
    {
      label: 'Сервер',
      title: 'Сервер',
      desc: 'Облачный сервер обрабатывает поступившие метрики, выполняет нейросетевой анализ и формирует логи',
      extraTitle: 'Анализ на базе ИИ',
      extraDesc: 'Мощные облачные серверы принимают потоки данных. Нейросетевые модели в реальном времени анализируют паттерны, выявляют аномалии и предсказывают возможные поломки оборудования до их возникновения.',
      icon: getIcon('downloadDoc')
    },
    {
      label: 'Приложение САНЭКС',
      title: 'Приложение САНЭКС',
      desc: 'Удобное мобильное и веб-приложение для мониторинга, мгновенных уведомлений и формирования отчетов',
      extraTitle: 'Единый центр управления',
      extraDesc: 'Пользователь получает доступ к интуитивно понятному дашборду. Система мгновенно рассылает PUSH-уведомления в случае критических отклонений. Доступна автоматическая генерация отчетов для руководства.',
      icon: getIcon('thumbsUp')
    }
  ];

  return (
    <section ref={sectionRef} className="py-16 md:py-24 bg-transparent overflow-hidden">
      <style>{`
        .circular-container { 
          --radius: 130px; 
          --label-offset: 50px; 
        }
        @media (min-width: 768px) { 
          .circular-container { 
            --radius: 210px; 
            --label-offset: 75px; 
          } 
        }
        @keyframes fadeInStep {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fade-in-step {
          animation: fadeInStep 0.8s ease-out forwards;
        }
      `}</style>

      <div className="container mx-auto px-4">
        {/* Header */}
        <div className="flex flex-col items-center justify-center text-center mb-2 md:mb-4 relative">
          <EditableText
            contentKey="how_it_works_title"
            defaultValue="Как это работает?"
            tagName="h2"
            className="text-3xl md:text-5xl font-bold font-playfair mb-4 text-gray-900 dark:text-white"
          />
          <EditableText
            contentKey="how_it_works_subtitle"
            defaultValue="Простой и понятный процесс работы системы Санэкс от установки датчиков до готового отчета"
            tagName="p"
            className="text-base md:text-lg text-gray-600 dark:text-gray-300 max-w-2xl mx-auto"
          />
          
          {isEditMode && (
            <div className="mt-4 flex flex-wrap justify-center gap-6 p-4 bg-gray-100/80 backdrop-blur-sm dark:bg-[#121214]/80 rounded-2xl border border-gray-200/50 dark:border-gray-800/50 shadow-sm relative z-40">
              <div className="flex items-center gap-3">
                <span className="text-sm font-medium text-gray-600 dark:text-gray-400">Цвет активности:</span>
                <ColorPicker isAbsolute={false} contentKey="how_works_active_color" defaultColor="#2563EB" onChange={setActiveColor} />
              </div>
              <div className="flex items-center gap-3">
                <span className="text-sm font-medium text-gray-600 dark:text-gray-400">Цвет кольца:</span>
                <ColorPicker isAbsolute={false} contentKey="how_works_ring_color" defaultColor="#E5E7EB" onChange={setRingColor} />
              </div>
              <div className="flex items-center gap-3">
                <span className="text-sm font-medium text-gray-600 dark:text-gray-400">Фон карточки:</span>
                <ColorPicker isAbsolute={false} contentKey="how_works_card_bg" defaultColor="#FFFFFF" onChange={setCardBg} />
              </div>
            </div>
          )}
        </div>

        {/* Flex Container for Menu and Info Card */}
        <div className="flex flex-col lg:flex-row items-center justify-center gap-8 lg:gap-12 mt-2 lg:mt-4">
          
          {/* Circular Menu */}
          <motion.div 
            className="relative flex items-center justify-center w-full lg:w-1/2 max-w-[650px] h-[450px] md:h-[650px] circular-container shrink-0"
            initial={{ scale: 0.8, opacity: 0, rotate: -180, filter: 'blur(10px)' }}
            animate={isInView ? { scale: 1, opacity: 1, rotate: 0, filter: 'blur(0px)' } : {}}
            transition={{ duration: 2.8, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
            onAnimationComplete={() => setIsAnimating(false)}
          >
            
            {/* Ring Border */}
            <div 
              className="absolute top-1/2 left-1/2 rounded-full border-2 border-dashed border-gray-300 dark:border-gray-700"
              style={{
                width: 'calc(var(--radius) * 2)',
                height: 'calc(var(--radius) * 2)',
                transform: `translate(-50%, -50%) rotate(${ringRotation}deg)`,
                transition: 'transform 1.6s cubic-bezier(0.34, 1.56, 0.64, 1)',
                maskImage: 'conic-gradient(black 0deg, black 288deg, transparent 288deg, transparent 360deg)',
                WebkitMaskImage: 'conic-gradient(black 0deg, black 288deg, transparent 288deg, transparent 360deg)',
                ...ringStyle
              }}
            ></div>

            {/* Center Card */}
            <div 
              className={`absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full shadow-2xl flex flex-col items-center justify-center text-center p-6 md:p-10 z-10 border border-gray-100 dark:border-white/10 transition-colors duration-500 w-[180px] h-[180px] md:w-[280px] md:h-[280px] ${!cardBg ? 'bg-white dark:bg-[#121214]' : ''}`}
              style={cardBg ? { backgroundColor: cardBg } : {}}
            >
              <div key={activeIndex} className="flex flex-col items-center justify-center animate-fade-in-step w-full">
                <EditableText
                  contentKey={`how_step_${activeIndex}_title`}
                  defaultValue={steps[activeIndex].title}
                  tagName="h3"
                  className="text-lg md:text-2xl font-bold mb-2 md:mb-4 text-gray-900 dark:text-white"
                />
                <EditableText
                  contentKey={`how_step_${activeIndex}_desc`}
                  defaultValue={steps[activeIndex].desc}
                  tagName="p"
                  className="text-[11px] md:text-sm text-gray-600 dark:text-gray-300 leading-relaxed"
                />
              </div>
            </div>

            {/* Steps */}
            {steps.map((step, i) => {
              const isActive = i === activeIndex;
              const isNext = i === maxUnlockedIndex + 1;
              const isLocked = i > maxUnlockedIndex + 1;

              return (
                <motion.div 
                  key={i} 
                  className="absolute top-1/2 left-1/2 w-0 h-0 z-20"
                  initial={{ opacity: 0, scale: 0.5 }}
                  animate={isInView ? { opacity: 1, scale: 1 } : {}}
                  transition={{ duration: 1.2, delay: i * 0.3, ease: "easeOut" }}
                >
                  {/* The Spoke */}
                  <div 
                    className="absolute top-0 left-0 w-0 h-0"
                    style={{ 
                      transform: `rotate(${i * 72 + ringRotation}deg)`,
                      transition: 'transform 1.6s cubic-bezier(0.34, 1.56, 0.64, 1)'
                    }}
                  >
                    {/* Icon Item */}
                    <div 
                      className={`absolute flex flex-col items-center justify-center group ${isLocked ? 'cursor-not-allowed opacity-40 grayscale' : 'cursor-pointer'}`}
                      style={{
                        transform: `translate(-50%, -50%) translateY(calc(-1 * var(--radius))) rotate(${-(i * 72 + ringRotation)}deg)`,
                        transition: 'transform 1.6s cubic-bezier(0.34, 1.56, 0.64, 1)'
                      }}
                      onClick={() => handleStepClick(i)}
                    >
                      {/* Badge */}
                      <div 
                        className={`absolute -top-2 -right-2 md:-top-3 md:-right-3 w-5 h-5 md:w-7 md:h-7 rounded-full flex items-center justify-center text-[10px] md:text-xs font-bold z-20 transition-colors duration-500
                          ${isActive 
                            ? 'text-white shadow-md' 
                            : isLocked
                              ? 'bg-gray-300 text-gray-500 dark:bg-gray-800 dark:text-gray-600'
                              : 'bg-gray-200 text-gray-600 dark:bg-gray-700 dark:text-gray-300 group-hover:bg-blue-100 group-hover:text-blue-600 dark:group-hover:bg-blue-900/50 dark:group-hover:text-blue-400'
                          }`}
                        style={isActive && activeColor ? { backgroundColor: activeColor } : (isActive ? { backgroundColor: '#2563EB' } : {})}
                      >
                        {i + 1}
                      </div>
                      
                      {/* Icon Circle */}
                      <div 
                        className={`w-12 h-12 md:w-20 md:h-20 rounded-full flex items-center justify-center transition-all duration-500 shadow-sm border-2 relative z-10
                          ${isActive 
                            ? (!activeColor ? 'scale-110 border-blue-600 bg-blue-600 text-white shadow-[0_0_25px_rgba(37,99,235,0.5)]' : 'scale-110 text-white') 
                            : isNext 
                              ? 'border-blue-400 bg-white dark:bg-gray-800 text-blue-500 animate-pulse' 
                              : isLocked
                                ? 'border-gray-200 bg-gray-100 dark:bg-gray-800 dark:border-gray-700 text-gray-400'
                                : 'border-gray-200 bg-white dark:bg-gray-800 dark:border-gray-700 text-gray-500 group-hover:border-blue-300 group-hover:scale-105'
                          }`}
                        style={isActive && activeColor ? { ...activeBgStyle, ...activeShadowStyle } : {}}
                      >
                        <div className={`flex items-center justify-center w-6 h-6 md:w-10 md:h-10 transition-all duration-500 ${isActive ? 'brightness-0 invert' : 'opacity-70 group-hover:opacity-100'}`}>
                          <EditableImage 
                            contentKey={`how_step_${i}_icon_img`} 
                            defaultSrc={step.icon} 
                            alt={step.title}
                            className="w-full h-full object-contain"
                          />
                        </div>
                      </div>
                    </div>

                    {/* Label Item */}
                    <div 
                      className="absolute flex items-center justify-center pointer-events-none z-30"
                      style={{
                        transform: `translate(-50%, -50%) translateY(calc(-1 * (var(--radius) + var(--label-offset)))) rotate(${-(i * 72 + ringRotation)}deg)`,
                        transition: 'transform 1.6s cubic-bezier(0.34, 1.56, 0.64, 1)'
                      }}
                    >
                      <div 
                        className={`w-24 md:w-32 text-center pointer-events-auto ${isLocked ? 'cursor-not-allowed' : 'cursor-pointer'} ${!isActive ? 'hidden md:block' : ''}`}
                        onClick={() => !isEditMode && handleStepClick(i)}
                      >
                        <EditableText
                          contentKey={`how_step_${i}_label`}
                          defaultValue={step.label}
                          tagName="span"
                          className={`text-[10px] md:text-sm font-medium transition-colors inline-block ${isActive ? 'text-blue-600 dark:text-blue-400 font-bold' : isLocked ? 'text-gray-400 dark:text-gray-600' : 'text-gray-500 dark:text-gray-400'}`}
                          style={isActive && activeColor ? activeTextStyle : {}}
                        />
                      </div>
                    </div>
                  </div>
                </motion.div>
              );
            })}

            {/* Mobile Navigation Arrows */}
            <div className="absolute top-1/2 left-2 right-2 -translate-y-1/2 flex justify-between md:hidden z-40 pointer-events-none">
              <button
                onClick={(e) => { e.stopPropagation(); handleStepClick(activeIndex - 1); }}
                disabled={activeIndex === 0}
                className={`pointer-events-auto w-10 h-10 sm:w-12 sm:h-12 rounded-full backdrop-blur-md border border-gray-200/50 dark:border-white/20 shadow-lg flex items-center justify-center transition-all duration-300 ${activeIndex === 0 ? 'opacity-40 cursor-not-allowed' : 'opacity-100 bg-white/80 dark:bg-white/10 hover:bg-white dark:hover:bg-white/20 active:scale-95'}`}
                aria-label="Предыдущий этап"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5 sm:w-6 sm:h-6 text-gray-700 dark:text-gray-300" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="15 18 9 12 15 6"></polyline>
                </svg>
              </button>
              
              <button
                onClick={(e) => { e.stopPropagation(); handleStepClick(activeIndex + 1); }}
                disabled={activeIndex === steps.length - 1}
                className={`pointer-events-auto w-10 h-10 sm:w-12 sm:h-12 rounded-full backdrop-blur-md border border-gray-200/50 dark:border-white/20 shadow-lg flex items-center justify-center transition-all duration-300 ${activeIndex === steps.length - 1 ? 'opacity-40 cursor-not-allowed' : 'opacity-100 bg-white/80 dark:bg-white/10 hover:bg-white dark:hover:bg-white/20 active:scale-95'}`}
                aria-label="Следующий этап"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5 sm:w-6 sm:h-6 text-gray-700 dark:text-gray-300" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={activeColor ? { color: activeColor } : { color: '#2563EB' }}>
                  <polyline points="9 18 15 12 9 6"></polyline>
                </svg>
              </button>
            </div>

          </motion.div>

          {/* Info Card (Right side) */}
          <motion.div 
            className={`w-full lg:w-1/2 max-w-[650px] p-8 pr-16 md:p-12 md:pr-24 rounded-3xl backdrop-blur-md border border-gray-200/50 dark:border-white/10 shadow-xl transition-colors duration-500 relative overflow-hidden min-h-[350px] flex flex-col justify-center ${!cardBg ? 'bg-white/60 dark:bg-[#121214]/60' : ''}`}
            style={cardBg ? { backgroundColor: cardBg } : {}}
            initial={{ x: 100, opacity: 0, filter: 'blur(10px)' }}
            animate={isInView ? { x: 0, opacity: 1, filter: 'blur(0px)' } : {}}
            transition={{ duration: 1.8, delay: 0.8, ease: [0.16, 1, 0.3, 1] }}
          >
            {/* Background Decorative Icon */}
            <div className="absolute -bottom-16 -right-16 w-64 h-64 opacity-[0.04] dark:opacity-[0.08] pointer-events-none transform rotate-12">
              <EditableImage 
                contentKey={`how_step_${activeIndex}_icon_img`} 
                defaultSrc={steps[activeIndex].icon} 
                alt=""
                className="w-full h-full object-contain"
              />
            </div>

            <div key={activeIndex} className="animate-fade-in-step relative z-10 flex flex-col h-full justify-center">
              <div className="mb-6 flex items-center gap-4">
                <div 
                  className="w-14 h-14 rounded-2xl flex items-center justify-center shrink-0 shadow-sm border border-gray-200/50 dark:border-white/10"
                  style={activeColor ? { backgroundColor: activeColor + '1A' } : { backgroundColor: 'rgba(37,99,235,0.1)' }}
                >
                  <div className="w-7 h-7 opacity-70 dark:opacity-90 dark:invert transition-all duration-300">
                    <EditableImage 
                      contentKey={`how_step_${activeIndex}_icon_img`} 
                      defaultSrc={steps[activeIndex].icon} 
                      alt=""
                      className="w-full h-full object-contain"
                    />
                  </div>
                </div>
                <EditableText
                  contentKey={`how_step_${activeIndex}_extra_title`}
                  defaultValue={steps[activeIndex].extraTitle}
                  tagName="h4"
                  className="text-xl md:text-3xl font-bold text-gray-900 dark:text-white leading-tight"
                />
              </div>
              
              <EditableText
                contentKey={`how_step_${activeIndex}_extra_desc`}
                defaultValue={steps[activeIndex].extraDesc}
                tagName="p"
                className="text-base md:text-lg text-gray-600 dark:text-gray-300 leading-relaxed"
              />
            </div>

            {/* Next Step Button */}
            <button
              onClick={handleNextStep}
              className="absolute right-3 md:right-6 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full backdrop-blur-md bg-white/60 dark:bg-white/10 border border-gray-200/50 dark:border-white/20 shadow-lg flex items-center justify-center transition-all duration-300 hover:scale-110 hover:bg-white/80 dark:hover:bg-white/20 z-20 group"
              aria-label="Следующий этап"
            >
              <svg 
                xmlns="http://www.w3.org/2000/svg" 
                className="w-6 h-6 transition-colors" 
                viewBox="0 0 24 24" 
                fill="none" 
                stroke="currentColor" 
                strokeWidth="2" 
                strokeLinecap="round" 
                strokeLinejoin="round" 
                style={activeColor ? { color: activeColor } : { color: '#2563EB' }}
              >
                <polyline points="9 18 15 12 9 6"></polyline>
              </svg>
            </button>
          </motion.div>

        </div>
      </div>

      <IOSToast 
        show={showToast} 
        message={toastMessage} 
        onClose={() => setShowToast(false)} 
      />
    </section>
  );
}